import React, { useEffect, useState } from 'react';
import Modal from '@sabre/spark-react-core/modal';
import { findIndex, isEmpty } from 'lodash';
import { bool, object } from 'prop-types';
import useApi from '../../../api-wrapper/use-api.js';
import {
   URL_SAVE_CRITERIA,
   URL_SAVE_CRITERIA_FILTER_OF_CONTEXT_ID,
   URL_SAVE_CRITERIA_FILTER_OF_USER_ID,
} from '../../../end-points.js';
import { DASHBOARD_TOP_TABS_VALUE } from '../../../utils/constants.js';
import { DateUtil } from '../../../utils/date-util.js';
import { getUserId } from '../app-configs-util/app-configs-util.js';
import {
   COMPETITION_SUMMARY_DATE_DIFF_THRESHOLD,
   generateUrlForSaveCriteria,
   RECOMMENDATION_SUMMARY_DATE_DIFF_THRESHOLD,
} from '../charts-and-grids/charts-util.js';
import SearchCriteriaModalContent from './search-criteria-modal-content.jsx';
import SearchCriteriaModalFooterContent from './search-criteria-modal-footer-content.jsx';
import {
   getDefaultStartEndDate,
   transformAddedRows,
} from './search-criteria-modal-util.js';

const SearchCriteriaModal = ({
   appConfigs,
   isOpen,
   isNotification,
   isErrorDateOutOfRange,
   recommCompetitor,
   searchCriteriaButtonOnClickHandler,
   searchCriteriaIconOnClickHandler,
}) => {
   const calendarFieldDateFormat = DateUtil.DATE_FORMATS.MONTH_DAY_YEAR;
   const userId = getUserId(appConfigs);
   const URL_SAVED_CRITERIA_SEARCH_USER_ID = `${URL_SAVE_CRITERIA}${URL_SAVE_CRITERIA_FILTER_OF_CONTEXT_ID}-${recommCompetitor}'${URL_SAVE_CRITERIA_FILTER_OF_USER_ID}'${userId}'`;
   const [savedCriteriaData, fetchSavedCriteriaData] = useApi({
      method: 'GET',
      url: URL_SAVED_CRITERIA_SEARCH_USER_ID,
   });
   const [savedCriteriaList, setSavedCriteriaList] = useState(
      savedCriteriaData.data.userSearch
   );
   const [savedSearchCriteriaData, fetchSavedSearchCriteriaData] = useApi([
      { method: 'POST', url: '', data: {} },
   ]);
   const [selectedSavedCriteria, setSelectedSavedCriteria] = useState(null);

   const [isOpenSearchModal, setIsOpenSearchModal] = useState(isOpen);
   const [isShowNotification, setIsShowNotification] = useState(isNotification);
   const [isDateOutOfRange, setIsDateOutOfRange] = useState(
      isErrorDateOutOfRange
   );
   const [isEmptyFieldPresent, setIsEmptyFieldPresent] = useState(false);
   const [searchCriteriaDetails, setSearchCriteriaDetails] = useState([]);
   const [startEndDate, setStartEndDate] = useState(
      getDefaultStartEndDate(calendarFieldDateFormat)
   );
   const [aggregationFromDate, setAggregationFromDate] = useState(
      DateUtil.formatDate(
         startEndDate.start,
         DateUtil.DATE_FORMATS.YEAR_MONTH_DAY
      )
   );
   const [aggregationToDate, setAggregationToDate] = useState(
      DateUtil.formatDate(
         startEndDate.end,
         DateUtil.DATE_FORMATS.YEAR_MONTH_DAY
      )
   );
   const [isClearSearchCriteria, setIsClearSearchCriteria] = useState(false);
   const [isSaveCriteriaExist, setIsSaveCriteriaExist] = useState(false);
   const [isSaveCriteriaSaved, setIsSaveCriteriaSaved] = useState(false);

   const getSavedCriteriaData = (savedCriteriaData) =>
      savedCriteriaData ? savedCriteriaData.data.userSearch : [];

   const setAggregationDateHandler = (start, end) => {
      setStartEndDate(
         getDefaultStartEndDate(calendarFieldDateFormat, start, end)
      );
      setAggregationFromDate(
         DateUtil.formatDate(start, DateUtil.DATE_FORMATS.YEAR_MONTH_DAY)
      );
      setAggregationToDate(
         DateUtil.formatDate(end, DateUtil.DATE_FORMATS.YEAR_MONTH_DAY)
      );
   };

   const handleSearchCriteriaModalCloseOnClick = () => {
      setIsOpenSearchModal(false);
      searchCriteriaIconOnClickHandler(false);
   };

   const handleSetSearchCriteria = (updatedSearchCriteria) =>
      setSearchCriteriaDetails([...updatedSearchCriteria]);

   const handleSearchCriteriaClearButtonOnClick = () => {
      const startEnd = getDefaultStartEndDate(calendarFieldDateFormat);
      setStartEndDate(
         getDefaultStartEndDate(
            calendarFieldDateFormat,
            startEnd.start,
            startEnd.end
         )
      );
      setAggregationFromDate(
         DateUtil.formatDate(
            startEnd.start,
            DateUtil.DATE_FORMATS.YEAR_MONTH_DAY
         )
      );
      setAggregationToDate(
         DateUtil.formatDate(startEnd.end, DateUtil.DATE_FORMATS.YEAR_MONTH_DAY)
      );
      setIsShowNotification(false);
      setIsDateOutOfRange(false);
      setIsEmptyFieldPresent(false);
      setIsClearSearchCriteria(!isClearSearchCriteria);
      setSelectedSavedCriteria('');
   };

   const handleSearchCriteriaSearchButtonOnClick = () => {
      if (
         findIndex(searchCriteriaDetails, (eachSearchCriteria) =>
            isEmpty(eachSearchCriteria.criteriaValue.selectedValues)
         ) > -1 ||
         isEmpty(aggregationFromDate) ||
         isEmpty(aggregationToDate)
      ) {
         setIsShowNotification(false);
         setIsEmptyFieldPresent(true);
         setIsDateOutOfRange(false);
         return;
      }

      if (
         DateUtil.difference(aggregationToDate, aggregationFromDate) >
         (DASHBOARD_TOP_TABS_VALUE.COMPETITION === recommCompetitor
            ? COMPETITION_SUMMARY_DATE_DIFF_THRESHOLD
            : RECOMMENDATION_SUMMARY_DATE_DIFF_THRESHOLD)
      ) {
         setIsShowNotification(false);
         setIsDateOutOfRange(true);
         setIsEmptyFieldPresent(false);
         return;
      }

      setIsEmptyFieldPresent(false);
      setIsDateOutOfRange(false);
      searchCriteriaButtonOnClickHandler(
         searchCriteriaDetails,
         aggregationFromDate,
         aggregationToDate
      );
   };

   const handleSearchCriteriaSaveButtonOnClick = (saveSearchCriteriaText) => {
      setIsShowNotification(false);
      if (!isEmpty(saveSearchCriteriaText)) {
         setIsEmptyFieldPresent(false);
         const transformedData = transformAddedRows(
            saveSearchCriteriaText,
            searchCriteriaDetails,
            startEndDate,
            userId,
            recommCompetitor
         );
         const url = generateUrlForSaveCriteria(
            savedCriteriaList,
            saveSearchCriteriaText,
            transformedData
         );
         setIsSaveCriteriaExist(url.method === 'PUT');
         if (url.method === 'POST') {
            fetchSavedSearchCriteriaData(url);
            setIsSaveCriteriaSaved(true);
            setSelectedSavedCriteria(saveSearchCriteriaText);
         }
      } else {
         setIsEmptyFieldPresent(true);
      }
   };

   const handleSearchCriteriaContinueButtonOnClick = (
      saveSearchCriteriaText
   ) => {
      setIsShowNotification(false);
      if (!isEmpty(saveSearchCriteriaText)) {
         setIsEmptyFieldPresent(false);
         const transformedData = transformAddedRows(
            saveSearchCriteriaText,
            searchCriteriaDetails,
            startEndDate,
            userId,
            recommCompetitor
         );
         const url = generateUrlForSaveCriteria(
            savedCriteriaList,
            saveSearchCriteriaText,
            transformedData
         );
         fetchSavedSearchCriteriaData(url);
         setIsSaveCriteriaExist(false);
         setIsSaveCriteriaSaved(true);
         setSelectedSavedCriteria(saveSearchCriteriaText);
      } else {
         setIsEmptyFieldPresent(true);
      }
   };

   const handleSearchCriteriaCancelButtonOnClick = () => {
      setIsSaveCriteriaExist(false);
   };

   useEffect(() => {
      setIsOpenSearchModal(isOpen);
   }, [isOpen]);

   useEffect(() => {
      setIsShowNotification(isNotification);
   }, [isNotification]);

   useEffect(() => {
      setIsDateOutOfRange(isErrorDateOutOfRange);
   }, [isErrorDateOutOfRange]);

   useEffect(() => {
      setSavedCriteriaList(getSavedCriteriaData(savedCriteriaData));
   }, [savedCriteriaData]);

   useEffect(() => {
      //TODO : We need to capture the success response of POST API and update the "savedSearchCriteriaData" when Search Criteria is Saved Successfully
      fetchSavedCriteriaData({
         method: 'GET',
         url: URL_SAVED_CRITERIA_SEARCH_USER_ID,
      });
   }, [savedSearchCriteriaData]);

   useEffect(() => {
      fetchSavedCriteriaData({
         method: 'GET',
         url: URL_SAVED_CRITERIA_SEARCH_USER_ID,
      });
   }, [recommCompetitor]);

   return (
      <Modal
         onClose={handleSearchCriteriaModalCloseOnClick}
         open={isOpenSearchModal}
         title={<div className='spark-mar-l-1'>Search Criteria</div>}
         titleHeadingLevel={3}
         modalFooter={
            <SearchCriteriaModalFooterContent
               isEmptyFieldPresent={isEmptyFieldPresent}
               setSelectedSavedCriteria={setSelectedSavedCriteria}
               searchCriteriaModalCloseOnClickHandler={
                  handleSearchCriteriaModalCloseOnClick
               }
               searchCriteriaClearButtonOnClickHandler={
                  handleSearchCriteriaClearButtonOnClick
               }
               searchCriteriaSearchButtonOnClickHandler={
                  handleSearchCriteriaSearchButtonOnClick
               }
               searchCriteriaSaveButtonOnClickHandler={
                  handleSearchCriteriaSaveButtonOnClick
               }
            />
         }
         modalId='dashboard-search-modal'
         className={`dashboard-search-modal${
            isOpenSearchModal ? '' : ' spark-hidden'
         }`}
         contentClassName='col-xs-12 col-sm-12 col-md-11 col-lg-8 col-xl-6'
      >
         <SearchCriteriaModalContent
            appConfigs={appConfigs}
            isShowNotification={isShowNotification}
            isDateOutOfRange={isDateOutOfRange}
            isSaveCriteriaExist={isSaveCriteriaExist}
            isSaveCriteriaSaved={isSaveCriteriaSaved}
            isClearSearchCriteria={isClearSearchCriteria}
            isEmptyFieldPresent={isEmptyFieldPresent}
            savedCriteriaList={savedCriteriaList}
            selectedSavedCriteria={selectedSavedCriteria}
            setSelectedSavedCriteria={setSelectedSavedCriteria}
            recommCompetitor={recommCompetitor}
            setSearchCriteriaHandler={handleSetSearchCriteria}
            setAggregationDateHandler={setAggregationDateHandler}
            searchCriteriaContinueButtonOnClickHandler={
               handleSearchCriteriaContinueButtonOnClick
            }
            searchCriteriaCancelButtonOnClickHandler={
               handleSearchCriteriaCancelButtonOnClick
            }
            startEndDate={startEndDate}
         />
      </Modal>
   );
};

SearchCriteriaModal.propTypes = {
   appConfigs: object.isRequired,
   isOpen: bool,
};

export default SearchCriteriaModal;
